import { Badge, Button, Tooltip } from '@ibsys/kit';
import { ArrowRightIcon, ListIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type KmAttrType, type KmAttrTypeOptions, useKmAttrTypes, useKmEntityTypeAttrs } from '@/entities/ontology';
import { getEntityTypeSection, useEntityTypeSamples } from './entityTypeSamples';
import { type OntologySectionColumn, OntologySectionTable } from './OntologySectionTable';

type Props = {
  code: string;
};

// Строка таблицы схемы: привязка атрибута, обогащённая данными справочника
type AttrRow = {
  attrTypeCode: string;
  name: string;
  description: string | null;
  dataType: string;
  isList: boolean;
  options: KmAttrTypeOptions | null;
  isRequired: boolean;
};

// Чипы вариантов для SELECT/MULTISELECT либо ссылка на тип-справочник; иначе пусто
const renderOptions = (options: KmAttrTypeOptions | null) => {
  if (!options) {
    return null;
  }
  if (options.choices?.length) {
    return (
      <div className="flex flex-wrap gap-1">
        {options.choices.map((choice) => (
          <Badge key={choice.code} variant="secondary" outline>
            {choice.label}
          </Badge>
        ))}
      </div>
    );
  }
  if (options.refEntityType) {
    return (
      <Badge variant="secondary" outline className="font-mono">
        {options.refEntityType}
      </Badge>
    );
  }
  return null;
};

// Есть ли у атрибута варианты справочника (choices) или ссылка на тип-справочник
const hasOptions = (options: KmAttrTypeOptions | null) =>
  Boolean(options?.choices?.length || options?.refEntityType);

/**
 * Секция «Атрибуты схемы» — только для чтения. Имя, тип данных, признак списка,
 * описание и настройки справочника подтягиваются из справочника атрибутов и
 * джойнятся к схеме по коду атрибута. Пустые колонки скрываются автоматически.
 */
export const EntityTypeAttrsSection: FC<Props> = ({ code }) => {
  const { t } = useTranslation(['ontology', 'common']);
  const navigate = useNavigate();
  const { data: attrsData } = useKmEntityTypeAttrs(code);
  const { data: attrTypesData } = useKmAttrTypes({ limit: 1000 });

  const rows = useMemo<AttrRow[]>(() => {
    const dict = new Map<string, KmAttrType>((attrTypesData?.items ?? []).map((a) => [a.code, a]));
    return (attrsData?.items ?? [])
      .filter((a) => !a.isDeleted)
      .map((a) => {
        const ref = dict.get(a.attrTypeCode);
        return {
          attrTypeCode: a.attrTypeCode,
          name: ref?.name ?? a.attrTypeCode,
          description: ref?.description ?? null,
          dataType: ref?.dataType ?? '',
          isList: ref?.isList ?? false,
          options: ref?.options ?? null,
          isRequired: a.isRequired,
        };
      });
  }, [attrsData, attrTypesData]);

  // Раздел приложения для типа сущности и примеры значений его атрибута `name` (TEXT)
  const section = getEntityTypeSection(code);
  const hasNameText = useMemo(
    () => rows.some((r) => r.attrTypeCode === 'name' && r.dataType === 'TEXT'),
    [rows],
  );
  const { data: samples } = useEntityTypeSamples(section && hasNameText ? code : undefined);

  const columns = useMemo<OntologySectionColumn<AttrRow>[]>(() => {
    // Строка атрибута `name`/TEXT, для которой показываем примеры значений раздела
    const isNameTextWithSection = (row: AttrRow) =>
      row.attrTypeCode === 'name' && row.dataType === 'TEXT' && Boolean(section);
    return [
      {
        key: 'name',
        header: t('ontology:attrName'),
        hasValue: (row) => Boolean(row.name),
        cell: (row) => row.name,
      },
      {
        key: 'code',
        header: t('ontology:attrCode'),
        // «Код» всегда в одну строку без переноса и обрезки
        cellClassName: 'whitespace-nowrap',
        hasValue: (row) => Boolean(row.attrTypeCode),
        cell: (row) => <span className="font-mono text-sm">{row.attrTypeCode}</span>,
      },
      {
        key: 'description',
        header: t('ontology:attrDescription'),
        hasValue: (row) => Boolean(row.description),
        cell: (row) =>
          row.description ? <span className="text-sm text-muted-foreground">{row.description}</span> : null,
      },
      {
        key: 'dataType',
        header: t('ontology:attrDataType'),
        hasValue: (row) => Boolean(row.dataType) || row.isList,
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1.5">
            {row.dataType && (
              <Badge variant="secondary" outline>
                {row.dataType}
              </Badge>
            )}
            {/* Признак «значение — список» пометкой рядом с типом данных */}
            {row.isList && (
              <Tooltip tooltip={t('ontology:attrListTooltip')}>
                <Badge variant="secondary" outline className="gap-1">
                  <ListIcon className="size-3" />
                  {t('ontology:attrIsList')}
                </Badge>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        key: 'values',
        header: t('ontology:attrOptions'),
        hasValue: (row) => isNameTextWithSection(row) || hasOptions(row.options),
        // Для `name`/TEXT — примеры реальных значений раздела и ссылка на раздел;
        // для остальных — варианты справочника.
        cell: (row) => {
          if (!isNameTextWithSection(row)) {
            return renderOptions(row.options);
          }
          return (
            <div className="flex flex-wrap items-center gap-1">
              {(samples ?? []).map((sample) => (
                <Badge key={sample} variant="secondary" outline>
                  {sample}
                </Badge>
              ))}
              {/* Синяя иконка «→» — переход в раздел приложения с полным списком значений */}
              <Button
                variant="ghost"
                size="icon"
                className="size-6 shrink-0"
                aria-label={t('ontology:openSection')}
                tooltip={t('ontology:openSection')}
                icon={<ArrowRightIcon className="size-4 text-primary" />}
                onClick={() => section && navigate(section.route)}
              />
            </div>
          );
        },
      },
      {
        key: 'required',
        header: t('ontology:attrRequired'),
        hasValue: () => true,
        cell: (row) => (
          <Badge variant={row.isRequired ? 'primary' : 'secondary'} outline>
            {row.isRequired ? t('common:yes') : t('common:no')}
          </Badge>
        ),
      },
    ];
  }, [t, section, samples, navigate]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <OntologySectionTable
      title={t('ontology:attrsSectionTitle')}
      columns={columns}
      rows={rows}
      getRowKey={(row) => row.attrTypeCode}
    />
  );
};
